import { ViewModel } from 'view_models/view_model'
import { Result } from 'entities/result'
import { QcType } from 'entities/protocol_version'
import { Badge } from 'react-bootstrap'

export type ResultInfo = {
    location?: string
    threshold?: number
    specificity?: number
    sensitivity?: number
    flag?: boolean
    converted_score?: number
    risk_ratio?: number
    max_risk_ratio?: number
    ppv?: number
    npv?: number
    prevalence?: number
    score?: number
    score_thres?: number
    stretch_out_raw_score?: number
    thres_high?: number
    thres_low?: number
}

export type RiskLevel = '高' | '中' | '低'
export class ResultViewModel extends ViewModel<Result> {
    public info = (): ResultInfo | undefined => {
        if (this.object.scoreJson) {
            return JSON.parse(this.object.scoreJson) as ResultInfo
        }
    }

    public allMarkers = () => {
        if (this.object.pcrProcessLogs) {
            return this.object.pcrProcessLogs ?? []
        } else return []
    }

    public riskMarkers = () => {
        return this.allMarkers()?.filter((pl: Record<string, any>) => pl.qc_type === null)
    }

    public hkMarkers = () => {
        return this.allMarkers()?.filter((pl: Record<string, any>) => pl.qc_type === 'HK')
    }

    public RnaSpikeInMarkers = () => {
        return this.allMarkers()?.filter((pl: Record<string, any>) => pl.qc_type === 'RNA_SPIKE_IN')
    }

    public RtSpikeInMarkers = () => {
        return this.allMarkers()?.filter((pl: Record<string, any>) => pl.qc_type === 'RT_SPIKE_IN')
    }

    /**
     * threshold lowを0、threshold highを1とおいたときのscoreの値を計算する
     * @returns stretched score
     */
    public stretchOutRawScore = (): number => {
        const Zh = 1
        const Zl = 0
        const i = this.info()
        const x = i!.score!
        const Th = i?.thres_high || 0.95 // thres_highがない場合はロジック導入当初の0.95を代入
        const Tl = i?.thres_low || 0.85 // thres_lowがないがない場合はロジック導入当初の0.85を代入
        const alpha = (Zh - Zl) / (Th - Tl)
        const y = alpha * (x - Tl) + Zl
        return y
    }

    /***
     * 詳細: https://drive.google.com/drive/folders/15PCqRVnT5hRT1FW2ab3oQ7u_ozAuoBM5
     */
    public convertViewScore = (): number => {
        const i = this.info()
        let max: number
        let min: number
        if (i?.thres_high && i?.thres_low) {
            // thres_lowを0としたときのscore最小値(0)の値を計算する。基本的にはこっちのルートを通る
            max = (1 - i.thres_low) / (i.thres_high - i.thres_low)
            min = -i.thres_low / (i.thres_high - i.thres_low)
        } else {
            // サーバー側のthres_highが存在しない場合があり、その場合はこのロジック導入当初のmin(-8.5)を代入する
            max = 1.5
            min = -8.5
        }

        const stretchedScore = i!.stretch_out_raw_score || this.stretchOutRawScore()
        const isOverThresHigh = stretchedScore - 1 > 0 // Threshold highを超えている
        const isOverThresLow = stretchedScore < 0 // Threshold lowを下回っている

        if (isOverThresHigh) {
            // ThresholdHighを超えている部分はmaxを2としてマッピングする
            const overThresHigh = stretchedScore - 1 // Threshold highから飛び出ている部分
            return 1 + overThresHigh / (max - 1)
        } else if (isOverThresLow) {
            // lowを下回っているのでstretchedScoreは負の値
            return -1 * (stretchedScore / min)
        } else {
            // Threshold low ~ highの間はそのままstretchedScoreを返す
            return stretchedScore
        }
    }

    public barPointRatio = (): number => {
        const viewScore = this.convertViewScore()
        if (viewScore > 0) return 0.25 * viewScore + 0.5
        else return 0.5 * viewScore + 0.5
    }

    public riskRatio = (): string | undefined => {
        const i = this.info()
        if (!i) return

        if (i.converted_score) {
            const percent = i.converted_score * 100.0
            if (this.riskLevel() === '低') {
                return `${Math.ceil(percent * 100) / 100}%程度`
            } else {
                if (percent >= 10) {
                    return '10%以上'
                } else {
                    return `${Math.ceil(percent * 10) / 10}%未満`
                }
            }
        } else if (i.prevalence) {
            const prevInPercent = Math.round(i.prevalence * 10000) / 100
            return `${prevInPercent}%未満`
        } else {
            return 'エラー'
        }
    }

    public riskLevel = (): RiskLevel => {
        const i = this.info()
        if (i && i.score) {
            if (i.score > 0.95) {
                return '高'
            } else if (i.score > 0.85) {
                return '中'
            } else {
                return '低'
            }
        } else {
            return '低'
        }
    }

    public riskComparison = (): string => {
        const i = this.info()
        if (i && i.risk_ratio) {
            if (i.max_risk_ratio && i.score && this.riskLevel() === '高') {
                return `${Math.round(i.max_risk_ratio)}倍以上`
            } else {
                return `${Math.round(i.risk_ratio)}倍`
            }
        } else {
            return '同程度'
        }
    }

    public numberOfPeople = (): string | undefined => {
        const i = this.info()
        if (i && i.prevalence) {
            return `${Math.round(i.prevalence * 100_000)}人`
        }
    }

    public part = () => {
        const pName = this.object.productItem?.name
        if (!pName) {
            throw new Error('おかしい')
        }
        return pName?.replaceAll('がん', '')
    }
}

type QcTypeBadgeProps = {
    qcType?: QcType
}

export const QcTypeBadge = (props: QcTypeBadgeProps) => {
    const { qcType } = props
    return (
        <Badge
            bg={(() => {
                if (qcType === 'RT_SPIKE_IN') {
                    return 'primary'
                } else if (qcType === 'RNA_SPIKE_IN') {
                    return 'success'
                } else if (qcType === 'HK') {
                    return 'warning'
                } else {
                    return 'secondary'
                }
            })()}
        >
            {qcType ?? 'PRODUCT'}
        </Badge>
    )
}
